/**
 * 공용 이미지 선택 컴포넌트.
 * 클릭/드래그&드롭 → 긴 변 1600px webp 클라이언트 변환 → 미리보기 + [사진 변경]/[되돌리기].
 * 프론트의 모든 이미지 업로드 UI 는 이 컴포넌트를 사용한다.
 */

const MAX_IMAGE_EDGE = 1600;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

// 소개 이미지: 등록 폼과 동일하게 긴 변 1600px webp 로 클라이언트 변환
const convertImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);

      let dataUrl = canvas.toDataURL('image/webp', 0.82);
      if (!dataUrl.startsWith('data:image/webp')) {
        dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      }
      resolve(dataUrl);
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지를 읽지 못했습니다. 다른 파일로 시도해 주세요.'));
    };

    image.src = url;
  });

// 파일을 변환 없이 data URL 로 읽는다 — 로고처럼 SVG · PNG 투명도를 그대로 지켜야 할 때
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () =>
      reject(new Error('이미지를 읽지 못했습니다. 다른 파일로 시도해 주세요.'));
    reader.readAsDataURL(file);
  });

/**
 * 이미지 선택 컴포넌트 (재사용).
 * 클릭/드래그&드롭으로 선택 → webp 변환 → 미리보기 교체 + [사진 변경]/[되돌리기] 버튼.
 * onStaged(dataUrl|null): 새 이미지가 준비되거나(dataUrl) 취소될 때(null) 호출.
 * side: 버튼을 사진 칸 오른쪽에 세로로 세운다(.uploader--side). 두 버튼 높이가 사진 칸
 *       높이를 이루므로 [되돌리기]를 감추지 않고 비활성으로 둔다.
 *
 * types: 받을 MIME 목록. 주면 그 형식만 받고 파일을 변환하지 않는다(원본 그대로 data URL).
 *        없으면 이미지면 무엇이든 받아 webp 로 변환한다.
 * maxBytes: 파일 크기 한도. desc: 빈 칸 안내 문구.
 *
 * 돌려주는 setInitial(url) 은 [되돌리기]가 돌아갈 원래 사진을 바꾸고 그 사진으로 되돌린다
 * (같은 모달로 여러 글을 고칠 때).
 */
const createImagePicker = ({
  initialUrl,
  onStaged,
  onError,
  side = false,
  types = null,
  maxBytes = MAX_IMAGE_BYTES,
  desc = '클릭하거나 파일을 끌어다 놓으세요',
}) => {
  const container = document.createElement('div');

  // [되돌리기]가 돌아갈 사진. setInitial() 로 바뀐다
  let initial = initialUrl || '';

  // 모양은 공통 업로더(.uploader)를 그대로 쓴다
  container.className = side ? 'uploader uploader--side' : 'uploader';

  const zone = document.createElement('div');
  zone.className = 'uploader__zone relative overflow-hidden';

  const img = document.createElement('img');
  img.alt = '이미지 미리보기';
  img.className = 'absolute inset-0 size-full object-contain';

  const placeholder = document.createElement('div');
  placeholder.className = 'pointer-events-none flex flex-col items-center gap-1';
  placeholder.innerHTML =
    '<span class="uploader__icon" aria-hidden="true"></span>' +
    '<span class="uploader__title">이미지 업로드</span>' +
    '<span class="uploader__desc"></span>';
  placeholder.querySelector('.uploader__desc').textContent = desc;

  const badge = document.createElement('span');
  badge.className = 'badge badge--info absolute top-2 left-2';
  badge.textContent = '변경됨 — 반영 시 교체';
  badge.hidden = true;

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = types ? types.join(',') : 'image/*';
  fileInput.hidden = true;

  const buttons = document.createElement('div');
  buttons.className = 'uploader__actions';

  const pickButton = document.createElement('button');
  pickButton.type = 'button';
  pickButton.className = 'button button--muted';

  const revertButton = document.createElement('button');
  revertButton.type = 'button';
  revertButton.className = 'button button--ghost';
  revertButton.textContent = '되돌리기';

  // 되돌릴 사진이 있을 때만 누를 수 있다 — 옆 배치는 자리를 지키고, 기본 배치는 아예 감춘다
  const setRevertable = (on) => {
    if (side) {
      revertButton.disabled = !on;
    } else {
      revertButton.hidden = !on;
    }
  };

  setRevertable(false);

  const showImage = (src) => {
    if (src) {
      img.src = src;
      img.hidden = false;
      placeholder.hidden = true;

      pickButton.textContent = '사진 변경';
    } else {
      img.removeAttribute('src');
      img.hidden = true;
      placeholder.hidden = false;

      pickButton.textContent = '사진 선택';
    }
  };

  const reset = () => {
    fileInput.value = '';
    badge.hidden = true;
    setRevertable(false);
    showImage(initial);
    onStaged(null);
  };

  const setInitial = (url) => {
    initial = url || '';
    reset();
  };

  const stageFile = async (file) => {
    if (!file) {
      return;
    }

    if (types ? !types.includes(file.type) : !file.type.startsWith('image/')) {
      onError(types ? '지원하지 않는 이미지 형식입니다.' : '이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    if (file.size > maxBytes) {
      onError(`이미지는 ${Math.floor(maxBytes / 1024 / 1024)}MB 이하만 가능합니다.`);
      return;
    }

    try {
      const dataUrl = types ? await readFileAsDataUrl(file) : await convertImage(file);
      showImage(dataUrl);
      badge.hidden = false;
      setRevertable(true);
      onStaged(dataUrl);
    } catch (error) {
      reset();
      onError(error.message);
    }
  };

  zone.addEventListener('click', () => fileInput.click());
  pickButton.addEventListener('click', () => fileInput.click());
  revertButton.addEventListener('click', reset);
  fileInput.addEventListener('change', () => stageFile(fileInput.files?.[0]));

  zone.addEventListener('dragover', (event) => {
    event.preventDefault();
    zone.classList.add('uploader__zone--over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('uploader__zone--over'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.classList.remove('uploader__zone--over');
    stageFile(event.dataTransfer?.files?.[0]);
  });

  showImage(initial);
  zone.append(img, placeholder, badge);
  buttons.append(pickButton, revertButton);
  container.append(zone, buttons, fileInput);

  return { element: container, reset, setInitial };
};

export { convertImage, createImagePicker };
